import { useMemo } from 'react';
import { getSessionParticipantsIds } from '../../features/session/session.selectors';
import { getMembersMap } from '../../features/realtime/realtime.selectors';
import { RealtimeMemberStatus } from '../../features/realtime/realtime.types';
import { getScreenshareState } from '../../features/screenshare/screenshare.selectors';
import { ScreenshareState } from '../../features/screenshare/screenshare.types';
import {
  getWaitingMember,
  type WaitingMember,
} from '../../shared/waitingMember';
import { useAppSelector } from '../useReduxToolkit';
import {
  anonymizeName,
  compareByJoinedAt,
  compareByOnHoldStatus,
} from './helpers';
import { useTestPatient } from '../useTestPatient';

export type UseProviderViewWaitingPatientsReturnType = {
  /**
   * All patients who are currently waiting:
   * - not yet joined the call (online)
   * - or were placed on hold
   */
  waitingPatientsMap: Record<string, WaitingMember>;
  waitingPatients: WaitingMember[];
  /**
   * Patients with whom a call has not yet started,
   * as well as those who are in the process of being added to a call.
   */
  onlinePatients: WaitingMember[];
  /**
   * Patients who previously joined the call but are currently on hold
   */
  onHoldPatients: WaitingMember[];
};

/**
 * Returns patients waiting for a provider call, including:
 * - online patients waiting to join
 * - on-hold patients waiting for the call to resume.
 */
export const useProviderViewWaitingPatients =
  (): UseProviderViewWaitingPatientsReturnType => {
    const realtimeMembersMap = useAppSelector(getMembersMap);
    const sessionParticipantsIds = useAppSelector(getSessionParticipantsIds);

    const screenshareState = useAppSelector(getScreenshareState);
    const isScreenSharing = screenshareState === ScreenshareState.InProgress;

    const waitingPatientsMap = useMemo(() => {
      const map: Record<string, WaitingMember> = {};

      for (const [id, member] of Object.entries(realtimeMembersMap)) {
        const isWaiting =
          !sessionParticipantsIds.includes(id) &&
          (member.state.status === RealtimeMemberStatus.Online ||
            member.state.status === RealtimeMemberStatus.OnHold);

        if (!isWaiting) continue;

        const waitingMember = getWaitingMember(member);

        if (isScreenSharing) {
          map[id] = {
            ...waitingMember,
            picture: undefined,
            state: {
              ...waitingMember.state,
              username: anonymizeName(waitingMember.state.username ?? ''),
            },
          };
        } else {
          map[id] = waitingMember;
        }
      }

      return map;
    }, [realtimeMembersMap, sessionParticipantsIds, isScreenSharing]);

    const { waitingMember: testWaitingMember } = useTestPatient();
    const waitingPatients = useMemo(
      () =>
        (testWaitingMember
          ? [testWaitingMember, ...Object.values(waitingPatientsMap)]
          : Object.values(waitingPatientsMap)
        )
          .sort(compareByJoinedAt)
          .sort(compareByOnHoldStatus),
      [waitingPatientsMap, testWaitingMember],
    );

    const onlinePatients = waitingPatients.filter(
      (patient) => patient.state.status === RealtimeMemberStatus.Online,
    );

    const onHoldPatients = waitingPatients.filter(
      (patient) => patient.state.status === RealtimeMemberStatus.OnHold,
    );

    return {
      waitingPatientsMap,
      waitingPatients,
      onlinePatients,
      onHoldPatients,
    };
  };
